import axios, { AxiosError } from "axios";

// Interfaces matching the original Angular service
export interface AIResponse {
  success: boolean;
  enabled?: boolean;
  analysis?: string;
  modifiedBpmn?: string;
  modifiedForm?: string;
  originalPrompt?: string;
  prompts?: Array<{
    id: string;
    title: string;
    description: string;
    template: string;
  }>;
  error?: string;
  timestamp: number;
  prompt?: string;
}

export interface AIRequest {
  bpmnXml: string;
  prompt?: string;
}

export interface SimilarProcess {
  id: string;
  name: string;
  nameSlug: string;
  description: string;
  approved?: boolean;
}

export interface SimilarForm {
  id: string;
  name: string;
  nameSlug: string;
  description: string;
  approved?: boolean;
}

export interface PromptTemplate {
  id: string;
  title: string;
  description: string;
  template: string;
}

// These endpoints match the original Angular environment variables
const API_BASE_URL =
  import.meta.env.MODE === "development"
    ? "/designer/api/ai"
    : "/designer/api/ai";
const DESIGNER_API_BASE_URL =
  import.meta.env.MODE === "development"
    ? "/designer/api"
    : "/designer/api";

const ENDPOINTS = {
  analyze: "/analyze-bpmn",
  modify: "/modify-bpmn",
  prompts: "/prompts",
  status: "/status",
};

// Get auth token from localStorage
const getAuthToken = (): string => {
  try {
    const tokenData = localStorage.getItem(
      "oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app",
    );
    if (tokenData) {
      const parsedData = JSON.parse(tokenData);
      return parsedData.access_token || "";
    }
    return "";
  } catch {
    return "";
  }
};

const axiosInstance = axios.create({
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

class AIServiceClass {
  // Utility method to clean BPMN XML
  private cleanBPMNXml(bpmnXml: string): string {
    let cleaned = bpmnXml.trim();

    // Remove "xml" prefix if it exists at the beginning
    if (cleaned.startsWith("xml")) {
      cleaned = cleaned.substring(3).trim();
      console.log('AI Service - Removed "xml" prefix');
    }

    // Remove "XML" prefix if it exists at the beginning
    if (cleaned.startsWith("XML")) {
      cleaned = cleaned.substring(3).trim();
      console.log('AI Service - Removed "XML" prefix');
    }

    // Ensure it starts with XML declaration if it's BPMN content
    if (!cleaned.startsWith("<?xml") && cleaned.includes("<bpmn:definitions")) {
      cleaned = '<?xml version="1.0" encoding="UTF-8"?>\n' + cleaned;
      console.log("AI Service - Added XML declaration");
    }

    return cleaned;
  }

  // Utility method to validate BPMN XML
  validateBPMN(bpmnXml?: string): boolean {
    console.log(
      "AI Service - Validating BPMN XML:",
      bpmnXml?.substring(0, 100) + "...",
    );

    // Allow empty BPMN for new workflow creation
    if (!bpmnXml || bpmnXml.trim().length === 0) {
      console.log("AI Service - BPMN XML is empty - allowing for new workflow creation");
      return true;
    }

    try {
      // Clean the BPMN XML before validation
      let cleanedXml = this.cleanBPMNXml(bpmnXml);
      console.log(
        "AI Service - Cleaned BPMN XML:",
        cleanedXml.substring(0, 100) + "...",
      );

      // Basic XML validation - check if it's well-formed XML and contains BPMN elements
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(cleanedXml, "text/xml");

      // Check for XML parsing errors
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        console.log("AI Service - BPMN XML parsing error:", parseError);
        return false;
      }

      // Check for BPMN definitions element (handle both namespaced and non-namespaced)
      const definitions = xmlDoc.getElementsByTagName("definitions");
      const bpmnDefinitions = xmlDoc.getElementsByTagName("bpmn:definitions");
      const bpmn2Definitions = xmlDoc.getElementsByTagName("bpmn2:definitions");

      if (definitions.length === 0 && bpmnDefinitions.length === 0 && bpmn2Definitions.length === 0) {
        console.log("AI Service - No definitions element found in BPMN XML");
        return false;
      }

      console.log("AI Service - BPMN XML validation passed");
      return true;
    } catch (error) {
      console.log("AI Service - BPMN XML validation error:", error);
      return false;
    }
  }

  // Utility method to extract process name from BPMN XML
  extractProcessName(bpmnXml: string): string {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(bpmnXml, "text/xml");

      const processes = xmlDoc.getElementsByTagName("process");
      if (processes.length > 0) {
        const process = processes[0];
        return (
          process.getAttribute("id") ||
          process.getAttribute("name") ||
          "Untitled Process"
        );
      }

      return "Untitled Process";
    } catch (error) {
      return "Untitled Process";
    }
  }

  private handleResponse(response: any): AIResponse {
    if (!response.data.success) {
      throw new Error(response.data.error || "AI service request failed");
    }
    return response.data;
  }

  private handleError(error: any): never {
    let errorMessage = "AI service error occurred";

    if (error instanceof AxiosError) {
      if (!error.response) {
        errorMessage =
          "Unable to connect to AI service. Please check if the service is running.";
      } else if (error.response.data?.error) {
        errorMessage = error.response.data.error;
      } else {
        errorMessage = `AI service error: ${error.response.status} ${error.response.statusText}`;
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }

  // --- API Endpoints ---

  async getStatus(): Promise<AIResponse> {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}${ENDPOINTS.status}`,
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  async getSuggestedPrompts(): Promise<AIResponse> {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}${ENDPOINTS.prompts}`,
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  async analyzeBPMN(bpmnXml: string): Promise<AIResponse> {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}${ENDPOINTS.analyze}`,
        { bpmnXml },
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  async modifyBPMN(bpmnXml: string, prompt: string): Promise<AIResponse> {
    let cleanedXml = this.cleanBPMNXml(bpmnXml);
    if (!this.validateBPMN(cleanedXml)) {
      throw new Error("Invalid BPMN XML structure");
    }

    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}${ENDPOINTS.modify}`,
        {
          bpmnXml: cleanedXml,
          prompt,
        },
      );
      const result = this.handleResponse(response);

      // Validate the modified BPMN before returning
      if (result.modifiedBpmn) {
        const cleanedModified = this.cleanBPMNXml(result.modifiedBpmn);
        if (!this.validateBPMN(cleanedModified)) {
          throw new Error("AI generated invalid BPMN XML. The XML is not well-formed.");
        }
        // Update with cleaned version
        result.modifiedBpmn = cleanedModified;
      }

      return result;
    } catch (error) {
      this.handleError(error);
    }
  }

  async findSimilarProcesses(
    processName: string,
    limit: number = 2,
  ): Promise<any> {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/similar-processes`,
        {
          params: { name: processName, limit },
        },
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async modifyBPMNWithContext(
    bpmnXml: string,
    prompt: string,
    similarProcesses: string[],
  ): Promise<AIResponse> {
    let cleanedXml = this.cleanBPMNXml(bpmnXml);
    if (!this.validateBPMN(cleanedXml)) {
      throw new Error("Invalid BPMN XML structure");
    }

    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/modify-bpmn-with-context`,
        {
          bpmnXml: cleanedXml,
          prompt,
          similarProcesses,
        },
      );
      const result = this.handleResponse(response);

      // Validate the modified BPMN before returning
      if (result.modifiedBpmn) {
        const cleanedModified = this.cleanBPMNXml(result.modifiedBpmn);
        if (!this.validateBPMN(cleanedModified)) {
          throw new Error("AI generated invalid BPMN XML. The XML is not well-formed.");
        }
        // Update with cleaned version
        result.modifiedBpmn = cleanedModified;
      }

      return result;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getProcessContent(nameSlug: string): Promise<any> {
    try {
      const response = await axiosInstance.get(
        `${DESIGNER_API_BASE_URL}/process-contents/byslug/${nameSlug}`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async analyzeForm(formJson: string): Promise<AIResponse> {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/analyze-form`,
        { formJson },
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  async modifyForm(formJson: string, prompt: string): Promise<AIResponse> {
    try {
      const response = await axiosInstance.post(`${API_BASE_URL}/modify-form`, {
        formJson,
        prompt,
      });
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  async findSimilarForms(formName: string, limit: number = 2): Promise<any> {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/similar-forms`,
        {
          params: { name: formName, limit },
        },
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async modifyFormWithContext(
    formJson: string,
    prompt: string,
    similarForms: string[],
  ): Promise<AIResponse> {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/modify-form-with-context`,
        {
          formJson,
          prompt,
          similarForms,
        },
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  async getFormContent(nameSlug: string): Promise<any> {
    try {
      const response = await axiosInstance.get(
        `${DESIGNER_API_BASE_URL}/form-contents/byslug/${nameSlug}`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }
}

export const aiService = new AIServiceClass();
